<template>
  <tr
    class="sv-table-row"
    :class="{ 'sv-table-row--all': row.allDone, 'sv-table-row--broken': broken }"
    :data-test="`sv-table-row-${row.aptNum}`"
  >
    <!-- 1. Time -->
    <td class="sv-table-time c-time">
      <template v-if="timeParts">
        <span class="t">{{ timeParts.time }}</span><span class="p">{{ timeParts.period }}</span>
        <span class="dur">{{ minutes }} min</span>
      </template>
      <span v-else class="t" data-test="sv-table-no-time">—</span>
    </td>

    <!-- 2. Operatory and provider -->
    <td class="c-op">
      <span class="sv-table-op" :data-test="`sv-table-op-${row.aptNum}`">{{ opName || '—' }}</span>
      <div v-if="providerShort" class="sv-table-prov" :data-test="`sv-table-prov-${row.aptNum}`">
        <span class="av" :style="{ '--pv': providerColor }" :data-test="`sv-table-av-${row.aptNum}`">{{ providerInitials }}</span>{{ providerShort }}
      </div>
    </td>

    <!-- 3. Patient, the visit, the carrier -->
    <td class="c-pt">
      <div class="sv-table-who">
        <button
          type="button"
          class="sv-table-name"
          :data-test="`sv-table-name-${row.aptNum}`"
          @click="$emit('select-appointment', appt)"
        >{{ name }}</button>
        <span v-if="ageText" class="sv-table-age" :data-test="`sv-table-age-${row.aptNum}`">{{ ageText }}</span>
        <span
          v-if="pillText"
          class="sv-table-st"
          :class="`sv-table-st--${pillKey}`"
          :data-test="`sv-table-status-${row.aptNum}`"
        >{{ pillText }}</span>
      </div>
      <div v-if="procedures" class="sv-table-procs" :data-test="`sv-table-procs-${row.aptNum}`">{{ procedures }}</div>
      <!--
        The appointment note (Open Dental's "Appointment Note"): ONE line with an ellipsis on screen,
        the full text on hover (title). On the PRINTED sheet it prints in full and wrapped — paper has
        no hover (ScheduleTableView's print block). Nothing for an empty or whitespace-only note.
      -->
      <div v-if="note" class="sv-table-note" :title="note" :data-test="`sv-table-note-${row.aptNum}`">{{ note }}</div>
      <!--
        The carrier and NOTHING else. /day carries the carrier's name and not whether it was verified,
        so no "verified" claim is printed (the insurance-verification alert already makes the to-do),
        and an empty name prints nothing — absent is "no primary plan found", never "Self-pay".
      -->
      <div v-if="carrier" class="sv-table-ins" :data-test="`sv-table-carrier-${row.aptNum}`">{{ carrier }}</div>
    </td>

    <!-- 4. Alerts: the visit checklist, inline -->
    <td class="sv-table-alerts c-alerts" :data-test="`sv-table-alerts-${row.aptNum}`">
      <span v-if="!alertsReady" class="sv-table-none" data-test="sv-table-still-checking">Still checking…</span>

      <div v-else-if="row.items.length" class="sv-table-list">
        <template v-for="it in row.items">
          <!-- A to-do: a real checkbox, the SAME tick (same key, same store row) as on the card. -->
          <button
            v-if="it.kind === 'action'"
            :key="it.id"
            type="button"
            role="checkbox"
            class="sv-table-item sv-table-item--act"
            :class="{ 'sv-table-item--done': it.checked }"
            :aria-checked="it.checked ? 'true' : 'false'"
            :data-alert="alertKey(it)"
            :data-test="`sv-table-item-${row.aptNum}-${it.id}`"
            @click="$emit('toggle', { aptNum: row.aptNum, itemKey: it.id, checked: !it.checked })"
          >
            <span class="sv-table-cb"><i v-if="it.checked" class="fa fa-check" aria-hidden="true"></i></span>
            <span class="sv-table-txt">
              <span v-if="it.tag" class="sv-table-tag" :class="`sv-table-tag--${it.cat}`">{{ it.tag }}</span>
              <span class="sv-table-t">{{ it.text }}</span>
              <span v-if="it.meta" class="sv-table-meta" :class="metaClass(it)">{{ it.meta.text }}</span>
            </span>
          </button>

          <!--
            An FYI: NOT a control. Premedication lives here — OD records that it is REQUIRED and
            nothing records that it was taken, so a checkbox would read as "taken".
          -->
          <div
            v-else
            :key="it.id"
            class="sv-table-item sv-table-item--info"
            :class="{ 'sv-table-item--crit': it.tone === 'crit' }"
            :data-alert="alertKey(it)"
            :data-test="`sv-table-info-${row.aptNum}-${it.id}`"
          >
            <span class="sv-table-fyi" :class="{ 'sv-table-fyi--crit': it.tone === 'crit' }" aria-hidden="true">!</span>
            <span class="sv-table-txt">
              <span v-if="it.tag" class="sv-table-tag" :class="`sv-table-tag--${it.cat}`">{{ it.tag }}</span>
              <span class="sv-table-t">{{ it.text }}</span>
              <span v-if="it.meta" class="sv-table-meta" :class="metaClass(it)">{{ it.meta.text }}</span>
            </span>
          </div>
        </template>
      </div>

      <!-- Said only when TRUE: a rule that failed to run is not "no alerts". -->
      <span v-else class="sv-table-none" :data-test="`sv-table-none-${row.aptNum}`">{{ incomplete ? 'Nothing found (some alerts could not be checked)' : 'No alerts' }}</span>
    </td>

    <!-- 5. Done: the ring on screen, a box and n/n on paper. Nothing when there is nothing to do. -->
    <td class="sv-table-done c-done">
      <template v-if="alertsReady && row.todo > 0">
        <span
          class="sv-table-ring"
          :class="{ 'sv-table-ring--all': row.allDone }"
          role="img"
          :title="ringTitle"
          :aria-label="ringTitle"
          :data-test="`sv-table-ring-${row.aptNum}`"
        >
          <svg width="36" height="36" viewBox="0 0 36 36" aria-hidden="true">
            <circle cx="18" cy="18" r="15" class="bg" />
            <circle
              cx="18"
              cy="18"
              r="15"
              class="fg"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="dashOffset"
              transform="rotate(-90 18 18)"
            />
          </svg>
          <span class="n"><i v-if="row.allDone" class="fa fa-check" aria-hidden="true"></i><template v-else>{{ row.done }}/{{ row.todo }}</template></span>
        </span>
        <span
          class="sv-table-pbox"
          :class="{ 'sv-table-pbox--on': row.allDone }"
          :data-test="`sv-table-pbox-${row.aptNum}`"
          aria-hidden="true"
        ><i v-if="row.allDone" class="fa fa-check"></i></span>
        <span class="sv-table-pdone" :data-test="`sv-table-pdone-${row.aptNum}`" aria-hidden="true">{{ row.done }}/{{ row.todo }}</span>
      </template>
    </td>
  </tr>
</template>

<script>
/**
 * One appointment as a row of the Table view (Phase 11): time, chair and provider, the patient and
 * the visit, the visit checklist's lines inline, and a Done ring.
 *
 * PRESENTATIONAL: props in, events out, no store. ScheduleTableView builds `row` (tableView.js
 * rowFor) from the page's own `checklistByAptNum` and decides what a tick does.
 *
 * ⛔ Nothing here builds a checklist. The lines arrive already ordered (clinical FYIs, other FYIs,
 * then to-dos by phase) and already marked `checked`; this file only draws them. The only thing it
 * takes from checklist.js is itemAlertKey, to label a line with the alert it came from.
 *
 * ⛔ `kind: 'info'` lines are never controls. Premedication is one (see the template).
 *
 * ⛔ No prop may be named `patNum`, `siteID`, `loading` or `allSettings`: mixin.js defines computeds
 * with those names and a computed SILENTLY REPLACES a same-named prop.
 *
 * ⛔ The demo mask is not applied here. Names arrive masked from the axios interceptor; Age and the
 * carrier stay real on purpose. Never mask at a call site.
 */
import { itemAlertKey } from '@/utils/scheduleView/checklist';
import {
  appointmentSpan, appointmentLength, formatMinutes, patientDisplayName,
} from '@/utils/scheduleView/layout';
import { appointmentNote } from '@/utils/scheduleView/apptNote';
import { shortProviderName } from '@/utils/scheduleView/tableView';
import { providerColorHex } from '@/utils/scheduleView/provColor';

const RING_RADIUS = 15;

export default {
  name: 'ScheduleTableRow',

  props: {
    /** One appointment from /schedule-view/day. */
    appt: { type: Object, required: true },
    /** tableView.js rowFor(): { aptNum, patNum, items, todo, done, fyi, allDone }. */
    row: { type: Object, required: true },
    /** The chair's name; empty for an appointment whose chair is not in the day. */
    opName: { type: String, default: '' },
    /** The provider record (FName, LName, Abbr) for this visit; null when unknown. */
    provider: { type: Object, default: null },
    /** The wording and bucket the block shows. */
    statusText: { type: String, default: '' },
    bucket: { type: String, default: 'unconfirmed' },
    broken: { type: Boolean, default: false },
    /** The alerts for this day have arrived. Until then the cell says so instead of "No alerts". */
    alertsReady: { type: Boolean, default: false },
    /** Some alert rules failed to run: an empty cell must not claim the visit is clean. */
    incomplete: { type: Boolean, default: false },
  },

  computed: {
    /** { time: '8:00', period: 'am' }, or null when the appointment has no usable start. */
    timeParts() {
      const span = appointmentSpan(this.appt);
      if (!span) return null;
      const parts = formatMinutes(span.start).split(' ');
      return { time: parts[0], period: parts[1] || '' };
    },

    minutes() {
      return appointmentLength(this.appt);
    },

    name() {
      return patientDisplayName(this.appt);
    },

    /** Only a real number: a missing age prints nothing, never "null" or "undefined". */
    ageText() {
      const age = this.appt && this.appt.patient ? this.appt.patient.Age : null;
      if (typeof age !== 'number' || !Number.isFinite(age)) return '';
      return String(age);
    },

    pillText() {
      return this.broken ? 'Broken' : this.statusText;
    },

    pillKey() {
      if (this.broken) return 'broken';
      return ['confirmed', 'arrived', 'completed', 'unconfirmed'].indexOf(this.bucket) !== -1
        ? this.bucket
        : 'unconfirmed';
    },

    /** ProcDescript is one comma-separated string; re-joined so the line wraps cleanly. */
    procedures() {
      return String((this.appt && this.appt.ProcDescript) || '')
        .split(',')
        .map(p => p.trim())
        .filter(Boolean)
        .join(', ');
    },

    /** The visit's note, or '' — the one definition (apptNote.js), shared by every surface. */
    note() {
      return appointmentNote(this.appt);
    },

    /** The carrier's name, or '' — a non-string or blank value prints nothing. */
    carrier() {
      const name = this.appt && this.appt.CarrierName;
      return typeof name === 'string' ? name.trim() : '';
    },

    providerShort() {
      return shortProviderName(this.provider);
    },

    /**
     * The ring round the provider's avatar: their Open Dental colour — the very colour their blocks
     * have for a stripe — or the neutral grey when they have none (provColor.js providerColorHex).
     */
    providerColor() {
      return providerColorHex(this.provider);
    },

    /** Two letters for the avatar: first name then surname, a "Dr." title skipped. */
    providerInitials() {
      const p = this.provider || {};
      const words = [p.FName, p.LName]
        .map(v => (v === null || v === undefined ? '' : String(v)))
        .join(' ')
        .replace(/,.*$/, '')
        .split(/\s+/)
        .filter(w => w && !/^dr\.?$/i.test(w));
      let letters = '';
      if (words.length > 1) letters = words[0].charAt(0) + words[words.length - 1].charAt(0);
      else if (words.length === 1) letters = words[0].slice(0, 2);
      else letters = String(p.Abbr || '').trim().slice(0, 2);
      return letters.toUpperCase();
    },

    circumference() {
      return 2 * Math.PI * RING_RADIUS;
    },

    /** The ring fills clockwise as to-dos are ticked: the unfilled part is what is left. */
    dashOffset() {
      const todo = this.row.todo || 0;
      if (!todo) return this.circumference;
      return this.circumference * (1 - Math.min(this.row.done, todo) / todo);
    },

    ringTitle() {
      return `${this.row.done} of ${this.row.todo} to-dos done`;
    },
  },

  methods: {
    alertKey(item) {
      return itemAlertKey(item.id);
    },

    metaClass(item) {
      return item.meta && item.meta.tone ? `sv-table-meta--${item.meta.tone}` : '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../tokens';

// ── the row ────────────────────────────────────────────────────────────────────────────────────────
.sv-table-row {
  td {
    padding: 10px;
    vertical-align: top;
    border-right: 1px solid $sv-line-2;
    border-bottom: 1px solid $sv-line-2;
    background: $sv-surface;
    transition: background 0.12s;

    &:last-child { border-right: 0; }
  }

  &:hover td { background: $sv-bg-2; }

  &--broken td { opacity: 0.55; }
  &--broken .sv-table-name { text-decoration: line-through; }
}

// `:hover` adds a class' worth of specificity, so the finished row's hover rule is spelled out and
// made HEAVIER than the plain one (two classes + :hover) — by specificity, not by source order —
// otherwise the pointer would swap its green wash for the plain hover tint.
.sv-table-row.sv-table-row--all td,
.sv-table-row.sv-table-row--all:hover td { background: $sv-row-done; }

// ── 1. time ───────────────────────────────────────────────────────────────────────────────────────
.sv-table-time {
  .t {
    font-size: 14px;
    font-weight: 800;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .p {
    margin-left: 3px;
    font-size: 11px;
    font-weight: 700;
    color: $sv-ink-3;
  }

  .dur {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    color: $sv-ink-3;
  }
}

// ── 2. chair and provider ─────────────────────────────────────────────────────────────────────────
.sv-table-op {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  box-sizing: border-box;
  height: 22px;
  padding: 0 9px;
  border-radius: $sv-radius-pill;
  // ONE neutral pill for every chair — no per-operatory colour anywhere on the page.
  background: $sv-bg-3;
  color: $sv-ink-2;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.sv-table-prov {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 6px;
  overflow: hidden;
  font-size: 11px;
  color: $sv-ink-3;
  text-overflow: ellipsis;
  white-space: nowrap;

  .av {
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    width: 18px;
    height: 18px;
    border-radius: 99px;
    // White, with a 2px ring in the provider's own colour (the stripe their blocks have) — the
    // design's `.hd-prov .av`. The ring is an inset shadow so it takes no room of its own.
    background: $sv-surface;
    box-shadow: inset 0 0 0 2px var(--pv, #{$sv-line});
    color: $sv-ink-2;
    font-size: 8px;
    font-weight: 800;
    line-height: 1;
  }
}

// ── 3. patient ────────────────────────────────────────────────────────────────────────────────────
.sv-table-who {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 7px;
}

.sv-table-name {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: $sv-ink;
  font: inherit;
  font-size: 13.5px;
  font-weight: 800;
  letter-spacing: -0.01em;
  text-align: left;
  cursor: pointer;

  &:hover {
    color: $sv-brand;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: 2px;
    border-radius: 3px;
  }
}

.sv-table-age {
  font-size: 11.5px;
  font-weight: 600;
  color: $sv-ink-3;
}

.sv-table-st {
  align-self: center;
  padding: 1px 6px;
  border-radius: 99px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

@each $key, $c in $sv-pill {
  .sv-table-st--#{$key} {
    color: map-get($c, shade);
    background: map-get($c, tint);
  }
}

.sv-table-procs {
  margin-top: 4px;
  font-size: 12px;
  color: $sv-ink-2;
}

// The appointment note: one line, an ellipsis (the column is `table-layout: fixed`, so the cell never
// widens for it); the full text is the `title`. The printed sheet overrides this to print it whole
// (ScheduleTableView.vue's print block — paper has no hover).
.sv-table-note {
  margin-top: 3px;
  overflow: hidden;
  font-size: 11.5px;
  font-style: italic;
  color: $sv-ink-3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sv-table-ins {
  margin-top: 3px;
  font-size: 11.5px;
  color: $sv-ink-3;
}

// ── 4. alerts ─────────────────────────────────────────────────────────────────────────────────────
.sv-table-none {
  font-size: 12px;
  color: $sv-ink-4;
}

.sv-table-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 3px 18px;
}

.sv-table-item {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  width: calc(100% + 8px);
  box-sizing: border-box;
  margin: 0 -4px;
  padding: 3px 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: $sv-ink;
  font: inherit;
  font-size: 12px;
  line-height: 1.35;
  text-align: left;
}

.sv-table-item--act {
  cursor: pointer;

  &:hover {
    background: $sv-bg-3;

    .sv-table-cb { border-color: $sv-brand; }
  }

  &:focus { outline: none; }
  &:focus-visible {
    outline: 2px solid $sv-brand-ring;
    outline-offset: -2px;
  }
}

.sv-table-cb {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 1px;
  box-sizing: border-box;
  border: 1.5px solid $sv-ink-4;
  border-radius: 4px;
  background: $sv-surface;
  color: #ffffff;
  transition: background 0.12s, border-color 0.12s;

  .fa { font-size: 9px; line-height: 1; }
}

.sv-table-fyi {
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  margin-top: 1px;
  border-radius: 99px;
  background: $sv-amber-soft;
  color: $sv-fyi-ink;
  font-size: 9.5px;
  font-weight: 800;
  line-height: 1;

  &--crit {
    background: $sv-rose;
    color: #ffffff;
  }
}

.sv-table-txt {
  min-width: 0;
}

.sv-table-tag {
  display: inline-block;
  margin-right: 5px;
  padding: 0 4px;
  border-radius: 4px;
  background: $sv-bg-3;
  color: $sv-ink-2;
  vertical-align: 1px;
  font-size: 8.5px;
  font-weight: 800;
  letter-spacing: 0.06em;
  line-height: 14px;
}

@each $key, $c in $sv-cat {
  .sv-table-tag--#{$key} {
    color: map-get($c, shade);
    background: map-get($c, tint);
  }
}

// ⛔ The title colours are written so the specificity never ties: the base is ONE class, and every
// state is two or three. (The visit-checklist card shipped with a ticked item still black because
// its done rule and its base rule tied and the later one won — found in a real browser, jsdom cannot
// see a cascade.)
.sv-table-t {
  color: $sv-ink;
}

.sv-table-item--info .sv-table-t { color: $sv-ink-2; }

.sv-table-item--info.sv-table-item--crit .sv-table-t {
  font-weight: 700;
  color: $sv-crit-ink;
}

.sv-table-item--done {
  .sv-table-cb {
    background: $sv-brand;
    border-color: $sv-brand;
  }

  .sv-table-t {
    color: $sv-ink-3;
    text-decoration: line-through;
    text-decoration-color: $sv-ink-4;
  }

  .sv-table-tag,
  .sv-table-meta { opacity: 0.5; }
}

.sv-table-meta {
  margin-left: 5px;
  color: $sv-ink-2;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10.5px;
  font-weight: 600;
  white-space: nowrap;

  &--late { color: $sv-late-ink; }

  &--amt {
    color: $sv-ink;
    font-weight: 700;
  }
}

// ── 5. done ───────────────────────────────────────────────────────────────────────────────────────
.sv-table-done {
  text-align: center;
}

.sv-table-ring {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;

  svg {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .bg {
    fill: none;
    stroke: $sv-line;
    stroke-width: 3;
  }

  .fg {
    fill: none;
    stroke: $sv-brand;
    stroke-width: 3;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.3s;
  }

  .n {
    position: relative;
    color: $sv-ink-2;
    font-size: 9.5px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
  }

  &--all {
    .bg { fill: $sv-status-confirmed; stroke: $sv-status-confirmed; }
    .fg { display: none; }
    .n { display: grid; color: #ffffff; }
  }
}

// The paper form of the Done cell: on screen it does not exist; ScheduleTableView's print block
// shows it (and hides the ring).
.sv-table-pbox,
.sv-table-pdone {
  display: none;
}
</style>
